import { MiddlewareAPI, Dispatch } from "redux";
import { trim, filter, orderBy, map, groupBy, forEach } from "../utils/lodash";
import { ActionModel as QuestionAction } from "../models/ActionModel";
import { StoreState } from "../models/states/StoreState";
import { PropertyType } from "../models/enums/PropertyType";
import { DispositionCode } from "../models/enums/DispositionCode";
import { ValueType } from "../models/enums/ValueType";
import { Piping } from "../piping";
import { QuestionModel } from "../models/QuestionModel";
import { Log } from "../utils/logging";
import {
    getPropertyString,
    getPropertyEnum,
    getPropertyBoolean,
} from "../models/utilities/propertyUtilities";
import { SelectionVariableModel } from "../models/SelectionVariableModel";
import { RecallType } from "../piping/RecallType";
import { __evaluateUnsafe__, __evaluateSafe__ } from "../expressions/parser";
import { getExitUrl } from "../models/utilities/actionUtilities";
import {
    navigationQuit,
    setVariableMentions,
    setLoopRow,
    audioRecordingStart,
    audioRecordingStop,
    navigationBranchTo,
    navigationNextPage,
} from "../redux/actions";
import { Randomizer } from "../randomization/Randomizer";

const TAG = "logic";

export const resumeLastSession = (
    store: MiddlewareAPI<Dispatch<any>, StoreState>,
    origin: QuestionModel,
    action: QuestionAction,
) => {
    const resumeQuestion = store.getState().interview.respondent.prevSessionLastAnsweredQuestion;
    if (resumeQuestion && resumeQuestion !== origin.name) {
        store.dispatch(navigationBranchTo({ origin: origin.name, destination: resumeQuestion }));
        store.dispatch(navigationNextPage());
        return true;
    }
};

export const branchTo = (
    store: MiddlewareAPI<Dispatch<any>, StoreState>,
    origin: QuestionModel,
    action: QuestionAction,
) => {
    const destination = getPropertyString(action, PropertyType.Question);
    if (destination) {
        const autoClean = getPropertyBoolean(action, PropertyType.AutoClean);
        const surveyResult = getPropertyEnum(action, DispositionCode, PropertyType.Status);
        const redirectUrl = getExitUrl(action, store.getState().interview);
        store.dispatch(
            navigationBranchTo({
                exitSurvey: surveyResult !== undefined,
                autoClean,
                origin: origin.name,
                destination,
                surveyResult,
                redirectUrl,
                isPreload: action.isPreload,
            }),
        );
        return true;
    }
};

export const exitSurvey = (
    store: MiddlewareAPI<Dispatch<any>, StoreState>,
    action: QuestionAction,
) => {
    const status = getPropertyEnum(action, DispositionCode, PropertyType.Status);
    const redirectUrl = getExitUrl(action, store.getState().interview);
    store.dispatch(navigationQuit(status, { redirectUrl }));
    return true;
};

export const setVariableValue = (
    store: MiddlewareAPI<Dispatch<any>, StoreState>,
    action: QuestionAction,
) => {};

export const computeVariable = (
    store: MiddlewareAPI<Dispatch<any>, StoreState>,
    action: QuestionAction,
) => {
    const variableName = getPropertyString(action, PropertyType.CalculatedVariableName);
    const valueType = getPropertyEnum(action, ValueType, PropertyType.ValueType);
    const value = getPropertyString(action, PropertyType.Value);
    let evaluated = value;
    if (valueType === ValueType.Formula)
        evaluated = __evaluateUnsafe__(
            () => store.getState().interview,
            store.dispatch,
            value,
        ).asString();
    else if (valueType === ValueType.Variable)
        evaluated = Piping.evaluateRecalls(store.getState().interview, value, RecallType.Code);
    // Dispatch the action.
    const variableValue = value === evaluated ? value : `${value} → ${evaluated}`;
    Log.d(TAG, "COMPUTE_VARIABLE", `${variableName}=${variableValue}`);
    store.dispatch(
        setVariableMentions(variableName, (mentions) => mentions.clear().add(evaluated)),
    );
};

export const computeSelections = (
    store: MiddlewareAPI<Dispatch<any>, StoreState>,
    action: QuestionAction,
) => {
    const { interview } = store.getState();
    const randomizer = new Randomizer(interview.respondent.seed);

    // Compute selection values.
    const a = filter(action.selections, (s) => {
        // Filter by inclusion formula.
        if (s.condition) return __evaluateSafe__(interview, s.condition).asBoolean();
        return true;
    });
    const b = orderBy(a, () => randomizer.next());
    const c = orderBy(b, (s) => s.score && __evaluateSafe__(interview, s.score).asNumber(), "desc");
    const d = orderBy(c, (s) => Number(s.priority) || 0, "desc");
    const selectionValues = map(d, (s) => trim(s.value));

    // Return set variable
    const groupedSelectionVariables = groupBy(action.selectionVariables, "name");
    forEach(groupedSelectionVariables, (variables: SelectionVariableModel[], name: string) => {
        const newMentions = variables.map(() => {
            const value = selectionValues.shift();
            return { value: value == null ? "" : value };
        });
        store.dispatch(setVariableMentions(name, (mentions) => mentions.clear().add(newMentions)));
    });
};

export const setloopRow = (
    store: MiddlewareAPI<Dispatch<any>, StoreState>,
    action: QuestionAction,
) => {
    const block = getPropertyString(action, PropertyType.Block);
    const valueType = getPropertyEnum(action, ValueType, PropertyType.ValueType);
    let value = getPropertyString(action, PropertyType.Value);
    if (valueType === ValueType.Variable)
        value = __evaluateUnsafe__(
            () => store.getState().interview,
            store.dispatch,
            value,
        ).asNumber();
    store.dispatch(setLoopRow(block, value));
};

export const mobileAudioRecording = (
    store: MiddlewareAPI<Dispatch<any>, StoreState>,
    action: QuestionAction,
) => {
    const { interview } = store.getState();
    const matrix = interview.navigation.currentPage.loopIndex;
    if (getPropertyBoolean(action, PropertyType.Mode, "Start"))
        store.dispatch(audioRecordingStart(action.questionName, matrix));
    else store.dispatch(audioRecordingStop());
};
